import { Component, HostListener, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Auth } from '@angular/fire/auth';
import { doc, Firestore, setDoc, getDoc } from '@angular/fire/firestore';
import {
  Storage,
  ref,
  uploadBytes,
  getDownloadURL,
} from '@angular/fire/storage';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { UxserviceService } from 'src/app/shared/services/uxservice.service';
import { MatDialog } from '@angular/material/dialog';
import { ImageCarouselDialogComponent } from '../profile/image-carousel-dialog/image-carousel-dialog.component';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';

import { onAuthStateChanged } from '@angular/fire/auth';
import { TermsDialogComponent } from 'src/app/components/terms-dialog/terms-dialog.component';
import { VendorsTermsComponent } from 'src/app/components/vendors-terms/vendors-terms.component';
import { GeminiService } from 'src/app/shared/services/gemini.service';


@Component({
  selector: 'app-role-setup',
  templateUrl: './role-setup.component.html',
  styleUrls: ['./role-setup.component.scss'],
})
export class RoleSetupPageComponent implements OnInit {
  type: 'customer' | 'vendor' = 'customer';
  businessType: string = '';
  returnUrl: string = '/';

  isSubmitting = false;
  selectedFile: File | null = null;

  formData: any = {
    firstname: '',
    accountdetails: {
      companyname: '',
      tradeLicenseNo: '',
      trnNumber: '',
      issuedBy: '',
      expiryDate: '',
      emailid: '',
      phoneNumber: '',
      telephone: '',
      countryCode: '+971',
      telephoneCountryCode: '+971',
      tradeLicenseCopyUrl: '',
      companyAddress: '',
      companyLogoUrl: '',
      companyLogoBase64: '',
      companyDocuments: [] as any[],
    },
  };

  aiExtractedLicenseNo: string = '';
  isLicenseMismatch: boolean = false;
  licenseMismatchError: string = '';


  // ── Calendar (for license expiry) ──
  showExpiryCalendar = false;
  expiryDate: Date | null = null;
  expiryDisplay: string | null = null;
  currentDate: Date = new Date();
  currentMonth: number = this.currentDate.getMonth();
  currentYear: number = this.currentDate.getFullYear();
  monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  calendarDays: any[] = [];

  get currentMonthName() {
    return this.monthNames[this.currentMonth];
  }

  years: number[] = [];

  generateYears() {
    const currentYear = new Date().getFullYear();
    this.years = [];
    for (let i = currentYear - 5; i <= currentYear + 20; i++) {
      this.years.push(i);
    }
  }

  selectMonth(month: number) {
    this.currentMonth = month;
    this.generateCalendar();
  }

  selectYear(year: number) {
    this.currentYear = year;
    this.generateCalendar();
  }

  onExpiryInput(event: any) {
    const value = event.target.value;
    // Format: DD/MM/YYYY
    const parts = value.split('/');
    if (parts.length === 3) {
      const d = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10) - 1;
      const y = parseInt(parts[2], 10);

      if (!isNaN(d) && !isNaN(m) && !isNaN(y) && y > 1900 && y < 2100) {
        const newDate = new Date(y, m, d);
        if (newDate.getDate() === d && newDate.getMonth() === m && newDate.getFullYear() === y) {
          this.expiryDate = newDate;
          this.currentMonth = m;
          this.currentYear = y;
          this.generateCalendar();
        }
      }
    }
  }

  // ── Country list ──
  countryList = [
    { name: 'UAE', code: '+971', flag: 'assets/flags/uae.png', limit: 9 },
    { name: 'India', code: '+91', flag: 'assets/flags/india.png', limit: 10 },
    {
      name: 'Saudi Arabia',
      code: '+966',
      flag: 'assets/flags/saudi.png',
      limit: 9,
    },
    { name: 'Kuwait', code: '+965', flag: 'assets/flags/kuwait.png', limit: 8 },
    { name: 'Oman', code: '+968', flag: 'assets/flags/oman.png', limit: 8 },
    { name: 'Qatar', code: '+974', flag: 'assets/flags/qatar.png', limit: 8 },
  ];

  get currentPhoneLimit(): number {
    const country = this.countryList.find(c => c.code === this.formData.accountdetails.countryCode);
    return country ? country.limit : 10;
  }

  get currentTelLimit(): number {
    const country = this.countryList.find(c => c.code === this.formData.accountdetails.telephoneCountryCode);
    return country ? country.limit : 10;
  }

  getSelectedCountry() {
    return this.countryList.find(
      (c) => c.code === this.formData.accountdetails.countryCode,
    );
  }

  getTelSelectedCountry() {
    return this.countryList.find(
      (c) => c.code === this.formData.accountdetails.telephoneCountryCode,
    );
  }

  numberOnly(event: any): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) return false;
    return true;
  }

  constructor(
    private auth: Auth,
    private afs: Firestore,
    private storage: Storage,
    private functions: Functions,
    private router: Router,
    private route: ActivatedRoute,
    private uxservice: UxserviceService,
    private dialog: MatDialog,
    private sanitizer: DomSanitizer,
    private geminiService: GeminiService,
  ) { }

  getLogoStyle(): SafeStyle {
    const url = this.formData.accountdetails.companyLogoUrl || '/assets/landingpage/Avatar.png';
    return this.sanitizer.bypassSecurityTrustStyle(`url('${url}')`);
  }

  termsAccepted = false;

  onTermsCheckboxChange(event: any) {
    if (event.checked) {
      // Force uncheck visually while dialog is open or until accepted
      event.source.checked = this.termsAccepted;
      this.openTermsDialog();
    } else {
      this.termsAccepted = false;
    }
  }

  openTermsDialog(event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    const dialogComponent: any = this.type === 'vendor' ? VendorsTermsComponent : TermsDialogComponent;


    const dialogRef = this.dialog.open(dialogComponent, {
      width: '1200px',
      panelClass: 'custom-dialog-container',
      backdropClass: 'blurred-backdrop',
      disableClose: true
    });


    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.termsAccepted = true;
      } else {
        this.termsAccepted = false;
      }
    });
  }

  triggerFileInput() {
    document.getElementById('logoInput')?.click();
  }

  async onLogoSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      try {
        this.uxservice.showOverlay('Optimizing & Uploading Logo...');
        
        // 1. Resize/Compress Image
        const optimizedBlob = await this.resizeImage(file, 400, 400);
        const optimizedFile = new File([optimizedBlob], file.name, { type: 'image/jpeg' });
        
        // 2. Convert to Base64
        const base64 = await this.fileToBase64(optimizedFile);

        // 3. Upload to Firebase Storage
        const filePath = `company_logos/${Date.now()}_logo.jpg`;
        const storageRef = ref(this.storage, filePath);
        await uploadBytes(storageRef, optimizedFile);
        const url = await getDownloadURL(storageRef);

        // Update Local State
        this.formData.accountdetails.companyLogoUrl = url;
        this.formData.accountdetails.companyLogoBase64 = base64;
        this.uxservice.showSuccess('Logo uploaded successfully!');
      } catch (error) {
        console.error('Error uploading logo:', error);
        this.uxservice.showError('Failed to upload logo.');
      } finally {
        this.uxservice.instantCloseOverlay();
      }
    }
  }

  private resizeImage(file: File, maxWidth: number, maxHeight: number): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event: any) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          if (width > height) {
            if (width > maxWidth) {
              height *= maxWidth / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width *= maxHeight / height;
              height = maxHeight;
            }
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);
          canvas.toBlob((blob) => {
            if (blob) resolve(blob);
            else reject(new Error('Canvas to Blob failed'));
          }, 'image/jpeg', 0.8);
        };
      };
      reader.onerror = (error) => reject(error);
    });
  }

  private fileToBase64(file: File | Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  }

  ngOnInit(): void {
    this.generateCalendar();
    this.generateYears();

    this.route.queryParams.subscribe(params => {
      this.type = params['type'] || 'customer';
      this.businessType = params['businessType'] || '';
      this.returnUrl =
        params['returnUrl'] || (this.type === 'vendor' ? '/business' : '/');
    });

    // Use auth state listener for reliable pre-filling on refresh
    onAuthStateChanged(this.auth, (user) => {
      if (user) {
        this.prefillUserData();
      }
    });
  }

  async prefillUserData() {
    const fbUser = this.auth.currentUser;
    if (fbUser) {
      try {
        const userRef = doc(this.afs, 'users-list', fbUser.uid);
        const userSnap = await getDoc(userRef);

        let firstname = fbUser.displayName
          ? fbUser.displayName.split(' ')[0]
          : '';
        let emailid = fbUser.email || '';
        let phoneData = '';

        if (userSnap.exists()) {
          const data: any = userSnap.data();
          firstname =
            data.firstname || data.displayName?.split(' ')[0] || firstname;
          emailid = data.email || emailid;
          phoneData = data.phoneNumber || data.phone || '';
        }

        this.formData.firstname = firstname;
        this.formData.accountdetails.emailid = emailid;

        // Auto-detect country code from phone string
        if (phoneData) {
          if (phoneData.startsWith('+')) {
            const matchedCountry = this.countryList.find((c) =>
              phoneData.startsWith(c.code),
            );
            if (matchedCountry) {
              this.formData.accountdetails.countryCode = matchedCountry.code;
              this.formData.accountdetails.phoneNumber = phoneData.replace(
                matchedCountry.code,
                '',
              );
            } else {
              this.formData.accountdetails.phoneNumber = phoneData;
            }
          } else {
            this.formData.accountdetails.phoneNumber = phoneData;
          }
        }
      } catch (e) {
        console.warn('Failed to prefill user data', e);
      }
    }
  }

  async submitForm(form: NgForm, e: Event) {
    e.preventDefault();
    if (form.invalid || !this.expiryDisplay || !this.termsAccepted) {
      Object.keys(form.controls).forEach((k) => form.controls[k]?.markAsTouched());
      this.uxservice.showError('Please fill all mandatory fields and accept terms');
      return;
    }

    const fbUser = this.auth.currentUser;
    if (!fbUser) {
      this.uxservice.showError('Session expired. Please log in again.');
      this.router.navigate(['/']);
      return;
    }

    this.isSubmitting = true;
    this.uxservice.showOverlay('Saving your business details...');

    if (this.formData.accountdetails.companyDocuments.length === 0) {
      this.isSubmitting = false;
      this.uxservice.instantCloseOverlay();
      this.uxservice.showError('Please upload your trade license file.');
      return;
    }

    try {
      const userRef = doc(this.afs, 'users-list', fbUser.uid);
      const licenseRef = doc(this.afs, 'tradeliceancedoc', fbUser.uid);
      const licenseSnapshot = await getDoc(licenseRef);
      const licenseData = licenseSnapshot.exists() ? licenseSnapshot.data() : null;

      const existingStatus = licenseData?.['status'];
      const existingApproved = licenseData?.['tradeLicenseApproved'];
      const statusToSet = existingStatus || 'pending';
      const approvedToSet = existingApproved !== undefined ? existingApproved : null;

      const phoneNumber = `${this.formData.accountdetails.countryCode}${this.formData.accountdetails.phoneNumber}`;
      const telephone = this.formData.accountdetails.telephone
        ? `${this.formData.accountdetails.telephoneCountryCode}${this.formData.accountdetails.telephone}`
        : '';

      const docUrl =
        this.formData.accountdetails.tradeLicenseCopyUrl ||
        (this.formData.accountdetails.companyDocuments?.[0]?.url || '');

      await setDoc(
        licenseRef,
        {
          uid: fbUser.uid,
          typedLicenseNumber: this.formData.accountdetails.tradeLicenseNo || '',
          typedExpiryDate: this.expiryDisplay || '',
          vendorName: this.formData.firstname || '',
          companyName: this.formData.accountdetails.companyname || '',
          email: this.formData.accountdetails.emailid || fbUser.email || '',
          phoneNumber: phoneNumber || '',
          documentUrl: docUrl,
          companyDocuments: this.formData.accountdetails.companyDocuments || [],
          status: statusToSet,
          tradeLicenseApproved: approvedToSet,
          verification: approvedToSet === true,
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );

      const tradeLicenseVerified = approvedToSet === true;
      const tradeLicenseApprovalStatus = statusToSet;
      const tradeLicenseApproved = approvedToSet;

      const dataToSave: any = {
        firstname: this.formData.firstname,
        displayname: this.formData.firstname,
        updatedAt: new Date().toISOString(),
        isVisiting: false, // finalized form
        companyName: this.formData.accountdetails.companyname,
        companyAddress: this.formData.accountdetails.companyAddress || '',
        businessLicenseNo: this.formData.accountdetails.tradeLicenseNo,
        tradeLicenseNumber: this.formData.accountdetails.tradeLicenseNo,
        trnNumber: this.formData.accountdetails.trnNumber || '',
        licenseIssuedBy: this.formData.accountdetails.issuedBy,
        licenseValidTill: this.expiryDate || this.expiryDisplay,
        tradeLicenseExpiry: this.expiryDisplay,
        companyDocuments: this.formData.accountdetails.companyDocuments || [],
        phoneNumber: phoneNumber,
        photoURL: this.formData.accountdetails.companyLogoUrl || '',
        companyLogoUrl: this.formData.accountdetails.companyLogoUrl || '',
        companyLogoBase64: this.formData.accountdetails.companyLogoBase64 || '',
        tradeLicenseVerified,
        tradeLicenseApprovalStatus,
        tradeLicenseApproved,
        tradeLicenseCopyUrl: docUrl,
        telephone: telephone, // Added telephone
        email: this.formData.accountdetails.emailid,
      };

      if (this.type === 'vendor') {
        dataToSave.role = 'business';
        dataToSave.isIndividual = false;
        dataToSave.accountdetails = {
          companyname: this.formData.accountdetails.companyname,
          tradeLicenseNo: this.formData.accountdetails.tradeLicenseNo,
          tradeLicenseNumber: this.formData.accountdetails.tradeLicenseNo,
          trnNumber: this.formData.accountdetails.trnNumber || '',
          issuedBy: this.formData.accountdetails.issuedBy,
          expiryDate: this.expiryDisplay,
          emailid: this.formData.accountdetails.emailid,
          phoneNumber: phoneNumber,
          telephone: telephone, // Added telephone
          tradeLicenseCopyUrl: docUrl,
          companyAddress: this.formData.accountdetails.companyAddress || '',
          companyLogoUrl: this.formData.accountdetails.companyLogoUrl || '',
          companyLogoBase64: this.formData.accountdetails.companyLogoBase64 || '',
          tradeLicenseVerified,
          tradeLicenseApprovalStatus,
          tradeLicenseApproved,
          businessType: this.businessType,
          companyDocuments: this.formData.accountdetails.companyDocuments || [],
        };
      } else {
        // Customer View (Business Customer)
        dataToSave.role = 'customer';
        dataToSave.isIndividual = false;

        // Also save in accountdetails object for consistency
        dataToSave.accountdetails = {
          companyname: this.formData.accountdetails.companyname,
          tradeLicenseNo: this.formData.accountdetails.tradeLicenseNo,
          tradeLicenseNumber: this.formData.accountdetails.tradeLicenseNo,
          issuedBy: this.formData.accountdetails.issuedBy,
          expiryDate: this.expiryDisplay,
          emailid: this.formData.accountdetails.emailid,
          phoneNumber,
          tradeLicenseCopyUrl: docUrl,
          companyAddress: this.formData.accountdetails.companyAddress || '',
          companyLogoUrl: this.formData.accountdetails.companyLogoUrl || '',
          companyLogoBase64: this.formData.accountdetails.companyLogoBase64 || '',
          tradeLicenseVerified,
          tradeLicenseApprovalStatus,
          tradeLicenseApproved,
          companyDocuments: this.formData.accountdetails.companyDocuments || [],
        };
      }

      await setDoc(userRef, dataToSave, { merge: true });
      this.uxservice.instantCloseOverlay();
      this.uxservice.showSuccess('Business profile updated successfully!');

      this.router.navigateByUrl(this.returnUrl);
    } catch (err: any) {
      this.uxservice.instantCloseOverlay();
      this.uxservice.showError(err.message);
      this.isSubmitting = false;
    }
  }

  onTradeLicenseNoChange() {}

  private normalizeLicenseNumber(value: unknown): string {
    return typeof value === 'string'
      ? value.trim().toUpperCase().replace(/[\s\-\/]/g, '')
      : '';
  }

  private fileToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  private async validateTradeLicenseWithAI(file: File): Promise<{ approved: boolean; reason: string | null }> {
    const dataUrl = await this.fileToDataUrl(file);
    let mimeType = file.type;
    if (!mimeType || mimeType === 'application/octet-stream') {
      const ext = file.name.split('.').pop()?.toLowerCase();
      if (ext === 'pdf') mimeType = 'application/pdf';
      else if (ext === 'png') mimeType = 'image/png';
      else if (ext === 'webp') mimeType = 'image/webp';
      else if (ext === 'jpg' || ext === 'jpeg') mimeType = 'image/jpeg';
      else mimeType = 'application/pdf';
    }

    type ValidateDocRequest = { fileBase64: string; mimeType: string };
    type ValidateDocResponse = { approved: boolean; reason: string | null; checks?: any };

    const validateFn = httpsCallable<ValidateDocRequest, ValidateDocResponse>(
      this.functions,
      'validateTradeLicense'
    );

    const result = await validateFn({
      fileBase64: dataUrl,
      mimeType,
    });
    return result.data;
  }

  async onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      // Size Restriction: 10MB
      const MAX_SIZE = 10 * 1024 * 1024; // 10MB
      if (file.size > MAX_SIZE) {
        this.uxservice.showError('File size exceeds the 10MB limit.');
        input.value = ''; // Clear the input
        return;
      }

      const allowedExts = ['pdf', 'jpg', 'jpeg', 'png', 'webp'];
      const fileExt = file.name.split('.').pop()?.toLowerCase() || '';
      if (!allowedExts.includes(fileExt)) {
        this.uxservice.showError('Please upload your Trade License in PDF, JPG, or PNG format.');
        input.value = '';
        return;
      }

      // Step 1: Scan and verify with Gemini AI
      this.uxservice.showOverlay('Scanning & verifying Trade License with AI...');

      try {
        const validationResult = await this.validateTradeLicenseWithAI(file);
        let isApproved = validationResult?.approved;

        // If AI couldn't read the license number cleanly, still allow upload
        // so the vendor can manually enter the license number and admin can review
        if (!isApproved && validationResult?.reason?.includes('reliably read a license number')) {
          isApproved = true;
        }

        if (!isApproved) {
          this.uxservice.instantCloseOverlay();
          const reason = validationResult?.reason || 'The uploaded file does not appear to be a valid Trade License. Please upload an official trade license document.';
          this.uxservice.showError(reason);
          input.value = '';
          return;
        }

        // Step 2: Upload upon successful verification
        this.uxservice.showOverlay('Trade License verified! Uploading...');

        const localUrl = URL.createObjectURL(file);
        const newDoc = {
          name: file.name,
          url: localUrl,
          path: '',
          savedAt: new Date().toISOString(),
          uploading: true,
        };
        this.formData.accountdetails.companyDocuments.push(newDoc);
        const index = this.formData.accountdetails.companyDocuments.length - 1;

        const filePath = `tradeLicenses/${Date.now()}_${file.name}`;
        const storageRef = ref(this.storage, filePath);
        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);

        this.formData.accountdetails.companyDocuments[index].url = downloadURL;
        this.formData.accountdetails.companyDocuments[index].path = filePath;
        this.formData.accountdetails.companyDocuments[index].uploading = false;

        // Update primary URL if it's the first one
        if (index === 0) {
          this.formData.accountdetails.tradeLicenseCopyUrl = downloadURL;
        }

        // Auto-fill extracted values if form fields are empty
        const ext = (validationResult as any)?.extracted;
        if (ext) {
          if (!this.formData.accountdetails.tradeLicenseNo && ext.licenseNumber) {
            this.formData.accountdetails.tradeLicenseNo = ext.licenseNumber;
          }
          if (!this.expiryDisplay && ext.expiryDate) {
            this.expiryDisplay = ext.expiryDate;
          }
          if (!this.formData.accountdetails.issuedBy && ext.issuingAuthority) {
            this.formData.accountdetails.issuedBy = ext.issuingAuthority;
          }
          if (!this.formData.accountdetails.companyname && ext.companyNameEnglish) {
            this.formData.accountdetails.companyname = ext.companyNameEnglish;
          }
        }

        const fbUser = this.auth.currentUser;
        if (fbUser) {
          const licenseRef = doc(this.afs, 'tradeliceancedoc', fbUser.uid);
          await setDoc(
            licenseRef,
            {
              uid: fbUser.uid,
              documentUrl: downloadURL,
              fileName: file.name,
              uploadedAt: new Date().toISOString(),
              licenseNumber: ext?.licenseNumber || '',
              expiryDate: ext?.expiryDate || '',
              companyNameEnglish: ext?.companyNameEnglish || '',
              issuingAuthority: ext?.issuingAuthority || '',
              status: 'pending',
              tradeLicenseApproved: null,
              verification: false,
            },
            { merge: true }
          );
        }

        this.uxservice.instantCloseOverlay();

      } catch (err: any) {
        console.error('AI verification / upload error:', err);
        this.uxservice.instantCloseOverlay();
        const errMsg = err?.message || 'Verification failed. Please ensure your document is clear and try again.';
        this.uxservice.showError(errMsg);
        input.value = '';
      }
    }
  }

  viewDocument(index: number) {
    const doc = this.formData.accountdetails.companyDocuments[index];
    if (!doc || !this.isImage(doc.name)) return;

    this.dialog.open(ImageCarouselDialogComponent, {
      data: {
        images: this.formData.accountdetails.companyDocuments.filter((d: any) =>
          this.isImage(d.name),
        ),
        initialIndex: this.formData.accountdetails.companyDocuments
          .filter((d: any) => this.isImage(d.name))
          .findIndex((d: any) => d.url === doc.url),
      },
      maxWidth: '95vw',
      maxHeight: '95vh',
      panelClass: 'image-viewer-dialog',
    });
  }

  removeDocumentByIndex(index: number) {
    this.formData.accountdetails.companyDocuments.splice(index, 1);
    // Sync primary URL if we removed the first one
    if (this.formData.accountdetails.companyDocuments.length > 0) {
      this.formData.accountdetails.tradeLicenseCopyUrl =
        this.formData.accountdetails.companyDocuments[0].url;
    } else {
      this.formData.accountdetails.tradeLicenseCopyUrl = '';
      this.selectedFile = null;
      const fileInput = document.getElementById(
        'licenseFileBusiness',
      ) as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    }
  }

  removeDocument() {
    this.formData.accountdetails.companyDocuments = [];
    this.formData.accountdetails.tradeLicenseCopyUrl = '';
    this.selectedFile = null;

    // Clear the file input
    const fileInput = document.getElementById(
      'licenseFileBusiness',
    ) as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  }

  isImage(filename: string): boolean {
    if (!filename) return false;
    const ext = filename.split('.').pop()?.toLowerCase();
    return ext === 'jpg' || ext === 'jpeg' || ext === 'png' || ext === 'webp' || ext === 'gif';
  }

  isDoc(filename: string): boolean {
    if (!filename) return false;
    const ext = filename.split('.').pop()?.toLowerCase();
    return ext === 'doc' || ext === 'docx';
  }

  goBack() {
    this.router.navigateByUrl(this.returnUrl);
  }

  // ── Calendar Logic ──
  @HostListener('document:click')
  closeCalendar() {
    this.showExpiryCalendar = false;
  }

  openExpiryCalendar(event: Event) {
    this.showExpiryCalendar = true;
    this.generateCalendar();
    if (!this.expiryDate) {
      const today = new Date();
      this.expiryDate = today;
      this.expiryDisplay = this.formatDisplayDate(today);
    }
    event.stopPropagation();
  }

  formatDisplayDate(date: Date): string {
    const d = String(date.getDate()).padStart(2, '0');
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const y = date.getFullYear();
    return `${d}/${m}/${y}`;
  }

  generateCalendar() {
    const days: any[] = [];
    const firstDay = new Date(this.currentYear, this.currentMonth, 1).getDay();
    const lastDate = new Date(
      this.currentYear,
      this.currentMonth + 1,
      0,
    ).getDate();
    const today = new Date();

    for (let i = 0; i < firstDay; i++) {
      days.push({ date: '', inactive: true, isToday: false, isPast: false, isSelected: false });
    }
    for (let d = 1; d <= lastDate; d++) {
      const isToday =
        this.currentYear === today.getFullYear() &&
        this.currentMonth === today.getMonth() &&
        d === today.getDate();
      const isPast =
        this.currentYear < today.getFullYear() ||
        (this.currentYear === today.getFullYear() && this.currentMonth < today.getMonth()) ||
        (this.currentYear === today.getFullYear() && this.currentMonth === today.getMonth() && d < today.getDate());

      const isSelected = this.expiryDate &&
        this.expiryDate.getDate() === d &&
        this.expiryDate.getMonth() === this.currentMonth &&
        this.expiryDate.getFullYear() === this.currentYear;

      days.push({ date: d, inactive: false, isToday, isPast, isSelected });
    }
    this.calendarDays = days;
  }

  prevMonth() {
    this.currentMonth--;
    if (this.currentMonth < 0) {
      this.currentMonth = 11;
      this.currentYear--;
    }
    this.generateCalendar();
  }

  nextMonth() {
    this.currentMonth++;
    if (this.currentMonth > 11) {
      this.currentMonth = 0;
      this.currentYear++;
    }
    this.generateCalendar();
  }

  selectExpiryDay(day: any) {
    if (day.inactive) return;
    this.expiryDate = new Date(this.currentYear, this.currentMonth, day.date);
    this.expiryDisplay = this.formatDisplayDate(this.expiryDate);
    this.showExpiryCalendar = false;
  }
}
